import { useState, useEffect } from 'react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useGoalProgress, useUpdateGoals } from '../../hooks/useGoals';
import { PageLoader } from '../../components/common/LoadingSpinner';
import { hideCurrency } from '../../utils/format';
import { useValuesVisibility } from '../../stores/valuesVisibilityStore';
import { ASSET_TYPE_LABELS, type AssetType, type Goal, type AllocationItem, type GoalProgress } from '../../types';
import toast from 'react-hot-toast';

const assetTypes = Object.keys(ASSET_TYPE_LABELS) as AssetType[];

export default function GoalsPage() {
  const { data: progress, isLoading } = useGoalProgress();
  const { mutate: updateGoals, isPending } = useUpdateGoals();
  const { isVisible } = useValuesVisibility();
  const [editMode, setEditMode] = useState(false);
  const [goalValues, setGoalValues] = useState<Record<string, number>>({});

  useEffect(() => {
    if (progress?.goals) {
      const vals: Record<string, number> = {};
      assetTypes.forEach((type) => {
        const goal = progress.goals.find((g: Goal) => g.assetType === type);
        vals[type] = goal?.targetPercentage ?? 0;
      });
      setGoalValues(vals);
    }
  }, [progress]);

  const handleSave = () => {
    const goals = assetTypes
      .filter((t) => goalValues[t] > 0)
      .map((t) => ({ assetType: t, targetPercentage: goalValues[t] }));

    const total = goals.reduce((s, g) => s + g.targetPercentage, 0);
    if (Math.abs(total - 100) > 0.01) {
      toast.error(`Total deve ser 100%. Atual: ${total.toFixed(1)}%`);
      return;
    }

    updateGoals({ goals }, { onSuccess: () => setEditMode(false) });
  };

  const total = Object.values(goalValues).reduce((s, v) => s + (v || 0), 0);

  const radarData = assetTypes.map((type) => {
    const target = goalValues[type] || 0;
    // 'percentage' field from portfolio allocation
    const alloc = progress?.currentAllocation?.find((a: AllocationItem) => a.type === type);
    const current = alloc?.currentPercentage ?? alloc?.percentage ?? 0;
    return {
      type: ASSET_TYPE_LABELS[type],
      target,
      current,
    };
  }).filter((d) => d.target > 0 || d.current > 0);

  if (isLoading) return <PageLoader />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Metas de Alocação</h1>
          <p className="page-subtitle">Configure sua alocação ideal e acompanhe o rebalanceamento</p>
        </div>
        {!editMode ? (
          <button onClick={() => setEditMode(true)} className="btn-primary">
            Editar Metas
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => setEditMode(false)} className="btn-secondary">
              Cancelar
            </button>
            <button onClick={handleSave} disabled={isPending} className="btn-primary">
              Salvar
            </button>
          </div>
        )}
      </div>

      {/* Status card */}
      {progress && (
        <div
          className={`card border ${
            progress.isBalanced
              ? 'border-success-500/30 bg-success-500/5'
              : 'border-warning-500/30 bg-warning-500/5'
          }`}
        >
          <div className="flex items-center gap-3">
            {progress.isBalanced ? (
              <CheckCircleIcon className="h-6 w-6 text-success-500 flex-shrink-0" />
            ) : (
              <ExclamationTriangleIcon className="h-6 w-6 text-warning-500 flex-shrink-0" />
            )}
            <div>
              <p className="font-medium text-white">
                {progress.isBalanced ? 'Portfólio Balanceado!' : 'Rebalanceamento Necessário'}
              </p>
              <p className="text-sm text-white/50">
                Valor total: {hideCurrency(progress.totalValue ?? 0, isVisible)}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Goals Editor / Display */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-white">Alocação Desejada</h2>
            {editMode && (
              <span
                className={`text-xs font-mono ${
                  Math.abs(total - 100) < 0.01
                    ? 'text-success-500'
                    : total > 100
                    ? 'text-danger-500'
                    : 'text-white/40'
                }`}
              >
                Total: {total.toFixed(1)}% / 100%
              </span>
            )}
          </div>

          <div className="space-y-3">
            {assetTypes.map((type) => {
              const target = goalValues[type] || 0;
              const alloc = progress?.currentAllocation?.find((a: AllocationItem) => a.type === type);
              const current = alloc?.currentPercentage ?? alloc?.percentage ?? 0;

              return (
                <div key={type}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white/70">
                      {ASSET_TYPE_LABELS[type]}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-white/40">
                        Atual: {current.toFixed(1)}%
                      </span>
                      {editMode ? (
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          max="100"
                          value={goalValues[type] ?? 0}
                          onChange={(e) =>
                            setGoalValues({
                              ...goalValues,
                              [type]: Number(e.target.value),
                            })
                          }
                          className="w-20 input text-right text-sm py-1"
                        />
                      ) : (
                        <span className="text-sm font-medium text-white w-16 text-right">
                          {target.toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </div>
                  {target > 0 && (
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-500 rounded-full transition-all"
                        style={{ width: `${Math.min(current / target * 100, 100)}%` }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Radar Chart */}
        {radarData.length > 0 && (
          <div className="card">
            <h2 className="text-sm font-semibold text-white mb-4">
              Comparativo: Meta vs Atual
            </h2>
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="rgba(255,255,255,0.1)" />
                <PolarAngleAxis
                  dataKey="type"
                  tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.5)' }}
                />
                <Radar
                  name="Meta"
                  dataKey="target"
                  stroke="#3b82f6"
                  fill="#3b82f6"
                  fillOpacity={0.2}
                />
                <Radar
                  name="Atual"
                  dataKey="current"
                  stroke="#22c55e"
                  fill="#22c55e"
                  fillOpacity={0.2}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload?.length && payload[0].payload) {
                      const d = payload[0].payload;
                      return (
                        <div className="bg-surface-800 border border-white/10 rounded-lg p-3 text-xs">
                          <p className="font-medium text-white">{d.type}</p>
                          <p className="text-primary-400">Meta: {d.target}%</p>
                          <p className="text-success-500">Atual: {d.current.toFixed(1)}%</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Rebalancing Table */}
      {progress?.rebalancing && progress.rebalancing.length > 0 && (
        <div className="card">
          <h2 className="text-sm font-semibold text-white mb-4">
            Sugestões de Rebalanceamento
          </h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th className="text-right">Meta</th>
                  <th className="text-right">Atual</th>
                  <th className="text-right">Diferença</th>
                  <th className="text-right">Valor</th>
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {progress.rebalancing.map((item: GoalProgress['rebalancing'][number]) => (
                  <tr key={item.type}>
                    <td>{ASSET_TYPE_LABELS[item.type as AssetType] || item.type}</td>
                    <td className="text-right">{item.targetPercentage.toFixed(1)}%</td>
                    <td className="text-right">{item.currentPercentage.toFixed(1)}%</td>
                    <td className={`text-right ${item.diff > 0 ? 'text-success-500' : item.diff < 0 ? 'text-danger-500' : 'text-white/40'}`}>
                      {item.diff > 0 ? '+' : ''}{item.diff.toFixed(1)}%
                    </td>
                    <td className={`text-right font-mono ${item.diffValue > 0 ? 'text-success-500' : item.diffValue < 0 ? 'text-danger-500' : 'text-white/40'}`}>
                      {item.diffValue !== 0 ? hideCurrency(Math.abs(item.diffValue), isVisible) : '—'}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          item.action === 'COMPRAR'
                            ? 'badge-green'
                            : item.action === 'VENDER'
                            ? 'badge-red'
                            : 'badge-blue'
                        }`}
                      >
                        {item.action}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
